/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
	PlantedComponent,
	isPlantMature,
} from '../../components/PlantingComponents';

import { GardenHudSystem } from './GardenHudSystem';
import { SavableObject } from '../../components/SaveDataComponents';
import { System } from 'ecsy';

// How often to re-check for newly-ripened plants (ms).
const CHECK_INTERVAL_MS = 15000;
// Never fire harvest notifications more than once per this window, so a big
// garden ripening in a burst is one gentle nudge, not a barrage.
const NOTIFY_THROTTLE_MS = 60000;
// Wait this long after the HUD appears before offering the opt-in, so it isn't
// the very first thing a new player sees.
const OPTIN_DELAY_MS = 8000;
const DISMISSED_KEY = 'gxs_notify_dismissed';

/**
 * Brings players back to their garden with harvest notifications. Growth is
 * wall-clock based, so a player's own plants ripen whether or not the tab is
 * focused; when the tab is hidden (backgrounded / phone locked with the app
 * still alive) and fresh plants become mature, this fires a single summary
 * notification through the service worker.
 *
 * It also owns the one-time permission opt-in prompt (shown on the HUD) and
 * service-worker registration. Fully-closed-tab delivery would need a push
 * server (VAPID) — that's a later, deployment-coupled step; this covers the
 * common "tabbed away / screen locked" case entirely client-side.
 *
 * UI lives on GardenHudSystem's overlay; this system reaches it through that
 * system rather than owning any DOM.
 */
export class GardenNotificationSystem extends System {
	init() {
		this._swRegistration = null;
		this._checkTimer = 0;
		this._lastNotifyMs = 0;
		this._hudShownAt = 0;
		this._optInWired = false;
		this._optInOffered = false;
		// Entity ids we've already counted as ripe, so we only notify on the
		// transition into "ready" (not every check while they sit ripe).
		this._notifiedIds = new Set();

		this._registerServiceWorker();
	}

	_registerServiceWorker() {
		if (!('serviceWorker' in navigator)) return;
		navigator.serviceWorker
			.register('service-worker.js')
			.then((reg) => {
				this._swRegistration = reg;
			})
			.catch((e) => console.warn('Service worker registration failed', e));
	}

	_supported() {
		return typeof Notification !== 'undefined';
	}

	_dismissed() {
		try {
			return window.localStorage.getItem(DISMISSED_KEY) === '1';
		} catch (e) {
			return false;
		}
	}

	_markDismissed() {
		try {
			window.localStorage.setItem(DISMISSED_KEY, '1');
		} catch (e) {
			// localStorage unavailable — just don't persist the dismissal.
		}
	}

	_overlay() {
		const hudSystem = this.world.getSystem(GardenHudSystem);
		return hudSystem ? hudSystem._overlay : null;
	}

	execute(delta) {
		if (!this._supported()) return;
		const overlay = this._overlay();
		if (!overlay || !overlay.root) return;

		// Wire the "Enable" button once the overlay exists.
		if (!this._optInWired && overlay.showNotifyOptIn) {
			this._optInWired = true;
			overlay.onEnableNotifications = (enable) => {
				if (enable) this._requestPermission();
				else this._markDismissed();
				overlay.hideNotifyOptIn();
			};
		}

		const hudVisible = overlay.root.style.display !== 'none';
		if (!hudVisible) {
			this._hudShownAt = 0;
			return;
		}
		if (!this._hudShownAt) this._hudShownAt = Date.now();

		this._maybeOfferOptIn(overlay);
		this._maybeNotify(delta);
	}

	_maybeOfferOptIn(overlay) {
		if (this._optInOffered) return;
		if (!overlay.showNotifyOptIn) return;
		if (Notification.permission !== 'default') return;
		if (this._dismissed()) return;
		if (Date.now() - this._hudShownAt < OPTIN_DELAY_MS) return;
		this._optInOffered = true;
		overlay.showNotifyOptIn();
	}

	_requestPermission() {
		try {
			const result = Notification.requestPermission();
			// Older Safari uses the callback form; the promise form is standard.
			if (result && typeof result.then === 'function') {
				result.catch(() => {});
			}
		} catch (e) {
			console.warn('Notification permission request failed', e);
		}
	}

	_maybeNotify(delta) {
		this._checkTimer += delta * 1000;
		if (this._checkTimer < CHECK_INTERVAL_MS) return;
		this._checkTimer = 0;

		// Only nudge when the player is away (tab hidden); if they're looking at
		// the HUD, the welcome-back card and mode UI already cover harvesting.
		if (!document.hidden) return;
		if (Notification.permission !== 'granted') return;

		const ripeIds = [];
		this.queries.ownPlants.results.forEach((entity) => {
			const planted = entity.getComponent(PlantedComponent);
			if (isPlantMature(planted)) ripeIds.push(entity.id);
		});

		const newlyRipe = ripeIds.filter((id) => !this._notifiedIds.has(id));
		// Keep the notified set in sync with what's actually ripe now (harvested
		// plants drop out, so a replant can notify again later).
		this._notifiedIds = new Set(ripeIds);

		if (newlyRipe.length === 0) return;
		if (Date.now() - this._lastNotifyMs < NOTIFY_THROTTLE_MS) return;
		this._lastNotifyMs = Date.now();

		const total = ripeIds.length;
		const s = total === 1 ? '' : 's';
		this._fireNotification(
			'🌼 Your garden is ready!',
			`${total} plant${s} ready to harvest. Come tend your garden.`,
		);
	}

	_fireNotification(title, body) {
		const options = {
			body,
			icon: 'assets/images/ui/brand/logo.png',
			badge: 'assets/images/ui/hud/harvest.png',
			tag: 'garden-harvest', // collapse repeats into one
			renotify: true,
		};
		try {
			if (this._swRegistration && this._swRegistration.showNotification) {
				this._swRegistration.showNotification(title, options);
			} else {
				// Desktop fallback when there's no active SW registration yet.
				// eslint-disable-next-line no-new
				new Notification(title, options);
			}
		} catch (e) {
			console.warn('Failed to show notification', e);
		}
	}
}

GardenNotificationSystem.queries = {
	ownPlants: { components: [PlantedComponent, SavableObject] },
};
